import React, { useEffect, useState } from "react";
import { Pages } from "@/lib/api";
import QuoteForm from "@/components/QuoteForm";
import Footer from "@/components/Footer";
import ReflectedHeading from "@/components/ReflectedHeading";
import EditableHTML from "@/components/EditableHTML";
import SporeBullet from "@/components/SporeBullet";
import SEO, { SITE_URL } from "@/components/SEO";

const INQUIRY_LIST = [
  "Thermification™ equipment packages",
  "Rapid water damage drying",
  "Mold-focused treatment support",
  "Disaster-response packages",
  "Generator-supported configurations",
  "Professional authorization",
  "Training and documentation",
  "Insurance program development",
  "Commercial applications",
  "Multi-unit and hotel applications",
  "Full-service restoration opportunities",
];

const PROPERTY_OWNER_ITEMS = [
  "When the water damage happened",
  "Where the water came from",
  "Whether the water source has been stopped",
  "What areas are affected",
  "Whether mold is visible",
  "Whether demolition has already started",
  "Whether power is available",
  "Photos or videos, if available",
  "Approximate size of the affected area",
];

const RESTORATION_ITEMS = [
  "Indirect-fired dry heat",
  "High-volume air movement",
  "Fresh-air exchange",
  "Ventilation",
  "Moisture documentation",
  "Particle counter and PID/VOC testing",
  "Disaster-response configurations",
  "Generator-supported drying",
  "Professional program development",
  "Insurance recognition pathways",
];

const Contact = () => {
  const [home, setHome] = useState({});
  useEffect(() => {
    (async () => {
      try { setHome((await Pages.get("home")).sections || {}); } catch { /* noop */ }
    })();
  }, []);

  return (
    <main className="min-h-screen text-slate-100 pt-32" data-testid="contact-page">
      <SEO
        title="Contact"
        description="Contact ATC Mold & Water — request Thermification™ information, restoration quotes, disaster-response, and professional partnership inquiries."
        path="/contact"
        breadcrumbs={[{ name: "Contact", path: "/contact" }]}
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: `${SITE_URL}/contact`,
          name: "Contact ATC Mold & Water Restoration",
        }}
      />
      {/* Hero */}
      <section className="water-section max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-5">
            Contact ATC Mold & Water
          </div>
          <ReflectedHeading
            as="h1"
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight chrome-gradient-text"
          >
            Water Damage Has a Clock. Start the Conversation Now.
          </ReflectedHeading>
          <p className="text-slate-300 mt-8 text-base sm:text-lg max-w-2xl leading-relaxed">
            If you are dealing with water damage, moisture concerns, mold concerns, storm damage,
            flood damage, power-loss drying needs, or large-scale restoration planning, contact
            ATC Mold &amp; Water to learn whether Thermification™ may be the right fit.
            Thermification™ is designed for qualified professionals seeking a faster,
            chemical-free, ozone-free, high-volume drying and air-exchange platform.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>

      {/* Inquiry list */}
      <section className="water-section py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <ReflectedHeading
            as="h2"
            className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mb-4"
          >
            Request Information About Thermification™
          </ReflectedHeading>
          <p className="text-slate-400 text-base mb-10">Contact us for information about:</p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {INQUIRY_LIST.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <SporeBullet size={22} className="mt-1 shrink-0" />
                <span className="text-slate-200">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>

      {/* Audience-specific guidance */}
      <section className="water-section py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-8">
          <div className="glass rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-4">For Property Owners</div>
            <h3 className="font-display font-black text-2xl text-white mb-4">Dealing with water damage?</h3>
            <p className="text-slate-300 mb-6">Contact us as soon as possible. Helpful information includes:</p>
            <ul className="space-y-3">
              {PROPERTY_OWNER_ITEMS.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <SporeBullet size={20} className="mt-0.5 shrink-0" />
                  <span className="text-slate-200 text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-400 text-sm mt-6 italic">
              The faster a wet structure is evaluated, the better the chance of drying affected
              materials before mold growth or demolition becomes a larger issue.
            </p>
          </div>

          <div className="glass rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-4">For Restoration Companies</div>
            <h3 className="font-display font-black text-2xl text-white mb-4">Interested in Thermification™?</h3>
            <p className="text-slate-300 mb-6">
              If you are a restoration, remediation, disaster-response, maintenance, property
              management, or environmental service company, contact us to learn more.
              Thermification™ is designed to support professional operators with:
            </p>
            <ul className="space-y-3">
              {RESTORATION_ITEMS.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <SporeBullet size={20} className="mt-0.5 shrink-0" />
                  <span className="text-slate-200 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>

      {/* Form */}
      <section id="contact" className="water-section py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <ReflectedHeading
              as="h2"
              className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mb-6"
            >
              Get In Touch
            </ReflectedHeading>
            <div className="space-y-5 text-slate-300 mt-8">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Phone</div>
                <EditableHTML html={home.contact_phone} className="text-lg" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Email</div>
                <EditableHTML html={home.contact_email} className="text-lg" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Service Area</div>
                <EditableHTML html={home.contact_address} className="text-sm text-slate-400" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Professional use notice */}
      <section className="water-section py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-4">Professional Use Notice</div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Thermification™ equipment is designed for professional use by trained operators.
              Mold remediation, water damage restoration, structural drying, demolition,
              reconstruction, disaster response, and environmental services may be subject to
              state, local, licensing, insurance, safety, and professional requirements.
              Operators are responsible for complying with all applicable requirements in the
              jurisdictions where services are performed.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mt-4 italic">
              Rapid drying. High-volume air exchange. No chemicals. No ozone. Thermification™
              is built to help qualified professionals respond faster, dry smarter, document
              better, and scale from individual water losses to disaster-response drying.
              Built for homes. Scaled for disasters.
            </p>
          </div>
        </div>
      </section>

      <Footer content={home} />
    </main>
  );
};

export default Contact;
